import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiPlus, FiMoreVertical, FiEdit, FiTrash2, FiUser, FiLoader, FiArrowLeft } from 'react-icons/fi';
import { partnersAPI } from '../services/api';
import './Partners.css';

const Partners = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPartners, setTotalPartners] = useState(0);

  // Fetch partners from API
  const fetchPartners = async (page = 1, search = '') => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page,
        limit: 10,
        ...(search && { search })
      };
      
      const response = await partnersAPI.getAll(params);
      
      if (response.success) {
        setPartners(response.partners);
        setTotalPages(response.totalPages);
        setTotalPartners(response.total);
        setCurrentPage(response.currentPage);
      } else {
        setError('Failed to fetch partners');
      }
    } catch (err) {
      console.error('Error fetching partners:', err);
      setError('Failed to load partners. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load partners on component mount
  useEffect(() => {
    fetchPartners();
  }, []);

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Debounce search
    const timeoutId = setTimeout(() => {
      fetchPartners(1, value);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      fetchPartners(page, searchTerm);
    }
  };

  // Handle partner deletion
  const handleDeletePartner = async (partnerId) => {
    if (window.confirm('Are you sure you want to delete this partner? This action cannot be undone.')) {
      try {
        const response = await partnersAPI.delete(partnerId);
        if (response.success) {
          // Refresh the list
          fetchPartners(currentPage, searchTerm);
        } else {
          alert('Failed to delete partner');
        }
      } catch (err) {
        console.error('Error deleting partner:', err);
        alert('Failed to delete partner. Please try again.');
      }
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  // Get partner full name
  const getPartnerName = (partner) => {
    return `${partner.firstName || ''} ${partner.lastName || ''}`.trim() || 'Unknown Partner';
  };

  // Get assigned extensions count
  const getExtensionsCount = (partner) => {
    return partner.assignedExtensions ? partner.assignedExtensions.length : 0;
  };

  // Get status badge class
  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'active';
      case 'inactive': return 'inactive';
      case 'suspended': return 'suspended';
      default: return 'inactive';
    }
  };

  // Simple function to handle menu click with boundary detection
  const handleMenuClick = (event) => {
    event.stopPropagation();
    const menuButton = event.currentTarget;
    const menu = menuButton.nextElementSibling;
    
    // Close all other menus first
    document.querySelectorAll('.menu-gen').forEach(m => {
      if (m !== menu) {
        m.style.display = 'none';
      }
    });
    
    // Toggle current menu
    if (menu && menu.classList.contains('menu-gen')) {
      if (menu.style.display === 'block') {
        menu.style.display = 'none';
      } else {
        // Get button position and tab container
        const buttonRect = menuButton.getBoundingClientRect();
        const tabContainer = menuButton.closest('.tab');
        const tabRect = tabContainer.getBoundingClientRect();
        const menuHeight = 200; // Approximate menu height
        
        // Check if there's enough space below the button within the tab container
        if (buttonRect.bottom + menuHeight + 5 < tabRect.bottom) {
          // Show menu below button (default behavior)
          menu.style.top = '100%';
          menu.style.bottom = 'auto';
        } else {
          // Show menu above button when near bottom of tab container
          menu.style.top = 'auto';
          menu.style.bottom = '100%';
        }
        
        menu.style.display = 'block';
      }
    }
  };

  // Function to close all menus when clicking outside
  const handleClickOutside = () => {
    document.querySelectorAll('.menu-gen').forEach(menu => {
      menu.style.display = 'none';
    });
  };

  // Add click outside listener
  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  if (loading && partners.length === 0) {
    return (
      <div className="partners-page">
        <div className="headerSection">
          <div className="header-content">
            <div className="header-left">
              <h2>Partners Management</h2>
              <p>Manage your partner accounts and their access to extensions</p>
            </div>
            <button className="back-btn" onClick={() => navigate('/dashboard')}>
              <FiArrowLeft />
              Back to Dashboard
            </button>
          </div>
        </div>
        <div className="dataArea-content">
          <div className="loading-container">
            <FiLoader className="loading-spinner" />
            <p>Loading partners...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="partners-page">
      <div className="headerSection">
        <div className="header-content">
          <div className="header-left">
            <h2>Partners Management</h2>
            <p>Manage your partner accounts and their access to extensions</p>
          </div>
          <button className="back-btn" onClick={() => navigate('/dashboard')}>
            <FiArrowLeft />
            Back to Dashboard
          </button>
        </div>
      </div>

      <div className="dataArea-content">
        <div className="tab">
          <div className="tabHeader d-flex">
            <div className="searchTab">
              <div className="search-gen">
                <input
                  className="form-control"
                  type="search"
                  placeholder="Search partners..."
                  value={searchTerm}
                  onChange={handleSearch}
                />
                <span className="bgForm">
                  <FiSearch className="search-ico" />
                </span>
              </div>
              <button 
                className="btn btn-blue btn-curved"
                onClick={() => navigate('/create-partner')}
              >
                <FiPlus />
                Add Partner
              </button>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger">
              {error}
            </div>
          )}

          <div className="tabBody">
            <div className="table-responsive">
              <table className="table table-gen">
                <thead>
                  <tr>
                    <th scope="col">Partner Name</th>
                    <th scope="col">Email</th>
                    <th scope="col">Company</th>
                    <th scope="col">Status</th>
                    <th scope="col">Extensions</th>
                    <th scope="col">Created At</th>
                    <th scope="col">Last Login</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {partners.map(partner => (
                    <tr key={partner._id}>
                      <td className="table-name">
                        <div className="partner-avatar">
                          <FiUser />
                        </div>
                        <div className="nameCellText">
                          <div>{getPartnerName(partner)}</div>
                        </div>
                      </td>
                      <td>{partner.email}</td>
                      <td>{partner.company || 'N/A'}</td>
                      <td>
                        <span className={`status-badge ${getStatusClass(partner.status)}`}>
                          {partner.status || 'Active'}
                        </span>
                      </td>
                      <td>{getExtensionsCount(partner)}</td>
                      <td className="table-date">{formatDate(partner.date)}</td>
                      <td className="table-date">{formatDate(partner.lastLogin)}</td>
                      <td>
                        <div className="menu-options">
                          <button className="btnOptions" data-title="Action" onClick={handleMenuClick}>
                            <FiMoreVertical />
                          </button>
                          <div className="menu-gen">
                            <button 
                              className="btn btn-medium"
                              onClick={() => navigate(`/partners/${partner._id}/edit`)}
                            >
                              <FiEdit />
                              Edit Partner
                            </button>
                            <button className="btn btn-medium">
                              <FiUser />
                              View End Users
                            </button>
                            <button 
                              className="btn btn-medium"
                              onClick={() => handleDeletePartner(partner._id)}
                            >
                              <FiTrash2 />
                              Delete Partner
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="paginationDiv">
              <ul className="pagination">
                <li className={currentPage === 1 ? 'disabled' : ''}>
                  <button 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-label="Go to previous page"
                  >
                    <span className="prevs">&lt; Prev</span>
                  </button>
                </li>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <li key={page} className={page === currentPage ? 'active' : ''}>
                    <button 
                      onClick={() => handlePageChange(page)}
                      aria-label={`Go to page number ${page}`}
                    >
                      {page}
                    </button>
                  </li>
                ))}
                
                <li className={currentPage === totalPages ? 'disabled' : ''}>
                  <button 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    aria-label="Go to next page"
                  >
                    <span className="nxts">Next &gt;</span>
                  </button>
                </li>
              </ul>
            </div>
          )}

          {partners.length === 0 && !loading && (
            <div className="no-data">
              <p>No partners found. {searchTerm && 'Try adjusting your search terms.'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Partners; 